/**
 * Normalize a raw ChessState object after JSON deserialization.
 * Fills in missing fields with safe defaults to prevent `.map()` on undefined crashes.
 *
 * Scope: normalizes all fields that contain arrays or nested objects used by UI
 * components (.map, .length, .filter). Optional scalar fields (capturedPieceTypes,
 * risingQueenCountdown, etc.) are passed through as-is since the engine checks
 * their existence before access.
 */

import type {
  AugmentationSelectionState,
  ChessState,
  PlayerAugmentations,
} from "../types";

function normalizePlayerAugmentations(
  raw: Partial<PlayerAugmentations> | undefined,
): PlayerAugmentations {
  return {
    active: raw?.active ?? [],
    pendingSelection: raw?.pendingSelection ?? null,
  };
}

function normalizeAugmentationSelection(
  raw: Partial<AugmentationSelectionState> | null | undefined,
): AugmentationSelectionState | null {
  if (raw == null) return null;
  return {
    whiteChoices: raw.whiteChoices ?? [],
    blackChoices: raw.blackChoices ?? [],
    whiteSelected: raw.whiteSelected ?? false,
    blackSelected: raw.blackSelected ?? false,
  };
}

/**
 * Compile-time guard: ensures the returned object satisfies ChessState.
 * If ChessState gains new required fields, this will produce a type error here,
 * reminding maintainers to add a default in normalizeChessState.
 */
function asChessState(s: ChessState): ChessState {
  return s;
}

export function normalizeChessState(raw: Record<string, unknown>): ChessState {
  const state = raw as Partial<ChessState>;

  return asChessState({
    id: state.id ?? "",
    version: state.version ?? "",
    board: state.board ?? { squares: [] },
    pieces: state.pieces ?? [],
    currentPlayer: state.currentPlayer ?? "WHITE",
    turnNumber: state.turnNumber ?? 1,
    phase: state.phase ?? "PLAYING",
    castlingRights: state.castlingRights ?? {
      whiteKingside: true,
      whiteQueenside: true,
      blackKingside: true,
      blackQueenside: true,
    },
    enPassantTarget: state.enPassantTarget ?? null,
    halfMoveClock: state.halfMoveClock ?? 0,
    positionHistory: state.positionHistory ?? [],
    actionHistory: state.actionHistory ?? [],
    consecutiveChecks: {
      white: state.consecutiveChecks?.white ?? 0,
      black: state.consecutiveChecks?.black ?? 0,
    },
    augmentations: {
      white: normalizePlayerAugmentations(state.augmentations?.white),
      black: normalizePlayerAugmentations(state.augmentations?.black),
    },
    augmentationSelection: normalizeAugmentationSelection(
      state.augmentationSelection,
    ),
    augmentationRound: state.augmentationRound ?? 0,
    capturedPieceTypes: state.capturedPieceTypes,
    risingQueenCountdown: state.risingQueenCountdown,
    minorRevivalCountdown: state.minorRevivalCountdown,
    queenMajestyLost: state.queenMajestyLost,
    weakenedKnightIds: state.weakenedKnightIds,
    createdAt: state.createdAt ?? "",
    updatedAt: state.updatedAt ?? "",
  });
}
